import React, { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, CheckCircle2, AlertTriangle, Package, Ruler, Plus, Loader2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

// Hangi ürünler "zip/sky zip" olarak tanınır?
const isZipItem = (item) => {
  const p = (item.product || "").toLowerCase();
  return p.includes("zip") || p.includes("sky") || p.includes("art ");
};

// Barkod içeriğinden sipariş no çıkar
// Format: "2601-1-K3" → orderNo="2601-1", boxNo=3
// Ya da: "ORD-2601-1-K1" → orderNo="ORD-2601-1", boxNo=1
// Ya da sadece sipariş no: "2601-1"
function parseBarcode(raw) {
  const v = (raw || "").trim();
  // -K{n} ile bitiyorsa koli barkodu
  const kMatch = v.match(/^(.+)-K(\d+)$/i);
  if (kMatch) return { orderNo: kMatch[1], boxNo: parseInt(kMatch[2]) };
  // Düz sipariş no
  return { orderNo: v, boxNo: null };
}

/* ------------------------------------------------------------------ */
/*  Kamera tarayıcı modal                                              */
/* ------------------------------------------------------------------ */
export function BarcodeScannerModal({ onClose, onScanned }) {
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const elementId = "zen-qr-reader";

  const startScanner = useCallback(async () => {
    try {
      const html5QrCode = new Html5Qrcode(elementId);
      scannerRef.current = html5QrCode;
      startedRef.current = false;
      setScanning(true);
      setError("");
      await html5QrCode.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: { width: 260, height: 120 }, aspectRatio: 1.5 },
        (decodedText) => {
          html5QrCode.stop().catch(() => {});
          startedRef.current = false;
          setScanning(false);
          onScanned(decodedText);
        },
        () => {}
      );
      startedRef.current = true;
    } catch (e) {
      startedRef.current = false;
      setError("Kamera erişilemedi. Tarayıcı izinlerini kontrol edin.");
      setScanning(false);
    }
  }, [onScanned]);

  useEffect(() => {
    startScanner();
    return () => {
      if (scannerRef.current && startedRef.current) {
        scannerRef.current.stop().catch(() => {});
        startedRef.current = false;
      }
    };
  }, [startScanner]);

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm bg-[#0F1623] border border-[#22304A] rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#22304A]">
          <div className="flex items-center gap-2">
            <ScanLine className="w-4 h-4 text-[#E5322A]" />
            <span className="text-sm font-bold text-white">Barkod Tara</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div
            id={elementId}
            className="w-full rounded-xl overflow-hidden bg-black"
            style={{ minHeight: 220 }}
          />

          {scanning && (
            <div className="flex items-center gap-2 text-xs text-slate-400 justify-center">
              <div className="w-2 h-2 rounded-full bg-[#E5322A] animate-pulse" />
              Koli etiketi üzerindeki barkodu kameraya gösterin
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-xs text-rose-400 bg-rose-500/10 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {!scanning && !error && (
            <button onClick={startScanner} className="w-full zen-btn-primary !text-sm">
              <Camera className="w-4 h-4" /> Tekrar Tara
            </button>
          )}

          <div className="text-[11px] text-slate-500 text-center">
            Sipariş koli etiketindeki barkodu okutun · Zip Perde & Sky Zip otomatik işlenir
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Barkod sonuç dialog: sipariş bulundu, çeki listesine ekle         */
/* ------------------------------------------------------------------ */
export function BarcodeResultDialog({ raw, onClose, onAddToPackingList }) {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [manual, setManual] = useState({ widthCm: "", heightCm: "", depthCm: 200, pieces: 1, netKg: 0, grossKg: 0 });
  const [manualMode, setManualMode] = useState(false);

  const { orderNo, boxNo } = parseBarcode(raw);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/orders");
        // Sipariş no ile eşleştir (orderNo veya id)
        const found = (data || []).find(o =>
          (o.orderNo || o.id || "").toLowerCase() === orderNo.toLowerCase() ||
          (o.id || "").toLowerCase() === orderNo.toLowerCase()
        );
        if (!found) throw new Error(`"${orderNo}" sipariş no bulunamadı`);
        setOrder(found);
      } catch (e) {
        setError(e.message || "Sipariş bulunamadı");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderNo]);

  const zipItems = order ? (order.items || []).filter(isZipItem) : [];
  const otherItems = order ? (order.items || []).filter(i => !isZipItem(i)) : [];
  const hasZip = zipItems.length > 0;
  const hasOther = otherItems.length > 0;

  const addZipToList = () => {
    if (!order) return;
    const boxes = zipItems.map((item, idx) => ({
      no: (boxNo || 1) + idx,
      widthCm: Math.round((item.widthCm || 0) / 10) || 20,  // mm → cm
      heightCm: Math.round((item.heightCm || 0) / 10) || 20,
      depthCm: 200,
      pieces: item.qty || 1,
      netKg: 0,
      grossKg: 0,
      content: `${item.product || "Zip Perde"}${item.fabric ? ` · ${item.fabric}` : ""}${item.widthCm ? ` · ${item.widthCm}×${item.heightCm}mm` : ""}`,
      note: item.color || "",
    }));
    onAddToPackingList(order, boxes);
    onClose();
    toast.success(`${zipItems.length} Zip kalem çeki listesine eklendi`);
  };

  const addManualToList = () => {
    if (!order) return;
    const content = otherItems.map(i => i.product || "Ürün").join(", ");
    onAddToPackingList(order, [{
      no: boxNo || 1,
      widthCm: Number(manual.widthCm) || 20,
      heightCm: Number(manual.heightCm) || 20,
      depthCm: Number(manual.depthCm) || 200,
      pieces: Number(manual.pieces) || 1,
      netKg: Number(manual.netKg) || 0,
      grossKg: Number(manual.grossKg) || 0,
      content,
      note: "",
    }]);
    onClose();
    toast.success("Koli çeki listesine eklendi");
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/75 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-lg zen-anim-up max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#22304A]">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-[#E5322A]" />
            <span className="font-bold text-sm">Tarama Sonucu</span>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Okunan barkod */}
          <div className="bg-slate-100 dark:bg-[#0C1220] rounded-xl p-3 text-xs font-mono text-slate-600 dark:text-slate-300">
            <span className="text-slate-400 mr-2">Okunan:</span>{raw}
            {boxNo && <span className="ml-2 zen-chip bg-slate-500/15 text-slate-500">Koli #{boxNo}</span>}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Sipariş aranıyor...
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-rose-500 bg-rose-500/10 rounded-xl p-4">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {order && (
            <div className="space-y-4">
              {/* Sipariş özeti */}
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 text-xs font-bold mb-2">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Sipariş Bulundu
                </div>
                <div className="text-sm font-bold">{order.orderNo || order.id}</div>
                <div className="text-xs text-slate-500">{order.customerName} · {order.projectName || "-"}</div>
                <div className="text-xs text-slate-500 mt-0.5">{(order.items || []).length} kalem</div>
              </div>

              {/* ZIP / SKY ZIP ürünler */}
              {hasZip && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Zip / Sky Zip Ürünler ({zipItems.length})
                  </div>
                  <div className="space-y-2">
                    {zipItems.map((item, i) => (
                      <div key={i} className="rounded-lg border border-slate-200 dark:border-[#22304A] p-3 text-xs">
                        <div className="font-semibold text-sm">{item.product}</div>
                        <div className="text-slate-500 mt-1 flex flex-wrap gap-3">
                          {item.widthCm && <span><Ruler className="w-3 h-3 inline mr-1" />{item.widthCm}×{item.heightCm} mm</span>}
                          {item.fabric && <span>{item.fabric}</span>}
                          {item.color && <span>{item.color}</span>}
                          <span>Adet: {item.qty || 1}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={addZipToList}
                    className="zen-btn-primary w-full !text-sm"
                    data-testid="barcode-add-zip-btn"
                  >
                    <Plus className="w-4 h-4" /> Çeki Listesine Ekle ({zipItems.length} koli)
                  </button>
                </div>
              )}

              {/* Diğer ürünler → ölçü gerekli */}
              {hasOther && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-amber-600 uppercase tracking-wider flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Ölçü Detayı Gerekli ({otherItems.length} ürün)
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-700 dark:text-amber-400">
                    {otherItems.map(i => i.product || "Ürün").join(", ")} ürünleri için koli ölçülerini manuel girin.
                  </div>

                  {!manualMode ? (
                    <button onClick={() => setManualMode(true)} className="zen-btn-ghost !text-xs w-full">
                      <Ruler className="w-3.5 h-3.5" /> Ölçüleri Gir
                    </button>
                  ) : (
                    <div className="space-y-3 rounded-xl border border-slate-200 dark:border-[#22304A] p-3">
                      <div className="text-xs font-semibold text-slate-500">Koli Ölçüleri (cm)</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[["widthCm", "En"], ["heightCm", "Boy"], ["depthCm", "Derinlik"]].map(([k, lbl]) => (
                          <div key={k}>
                            <label className="text-[10px] text-slate-400 block mb-1">{lbl}</label>
                            <input type="number" className="zen-input !text-xs !py-1 text-center"
                              value={manual[k]} onChange={(e) => setManual(p => ({ ...p, [k]: e.target.value }))} />
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {[["pieces", "Adet"], ["netKg", "Net kg"], ["grossKg", "Brüt kg"]].map(([k, lbl]) => (
                          <div key={k}>
                            <label className="text-[10px] text-slate-400 block mb-1">{lbl}</label>
                            <input type="number" step="0.01" className="zen-input !text-xs !py-1 text-center"
                              value={manual[k]} onChange={(e) => setManual(p => ({ ...p, [k]: e.target.value }))} />
                          </div>
                        ))}
                      </div>
                      <button onClick={addManualToList} className="zen-btn-primary w-full !text-xs" data-testid="barcode-add-manual-btn">
                        <Plus className="w-3.5 h-3.5" /> Ekle
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
